import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { useBlocker } from "react-router-dom"

interface Props {
  title: string
  dirty: boolean
  busy: boolean
  onClose: () => void
  onBack?: () => void
  children: ReactNode
}
export default function GiftDialog({ title, dirty, busy, onClose, onBack, children }: Props) {
  const dialog = useRef<HTMLDialogElement>(null)
  const heading = useRef<HTMLHeadingElement>(null)
  const [discarding, setDiscarding] = useState(false)
  const blocker = useBlocker(dirty || busy)
  useEffect(() => {
    const element = dialog.current!
    const scrollY = window.scrollY
    const overflow = document.body.style.overflow
    element.showModal()
    heading.current?.focus()
    document.body.style.overflow = "hidden"
    return () => {
      element.close()
      document.body.style.overflow = overflow
      window.scrollTo(0, scrollY)
    }
  }, [])
  useEffect(() => {
    function warn(event: BeforeUnloadEvent) {
      if (dirty || busy) { event.preventDefault(); event.returnValue = "" }
    }
    window.addEventListener("beforeunload", warn)
    return () => window.removeEventListener("beforeunload", warn)
  }, [dirty, busy])
  const confirming = discarding || blocker.state === "blocked"
  useEffect(() => { if (!confirming) heading.current?.focus() }, [title, confirming])
  function requestClose() { if (onBack) { onBack(); return } if (!busy) { if (dirty) setDiscarding(true); else onClose() } }
  return <dialog ref={dialog} className="gift-dialog" aria-labelledby="gift-dialog-title" onCancel={event => { event.preventDefault(); requestClose() }}>
    <header className="p-4 border-b border-gray-200 flex items-center justify-between shrink-0">
      <h2 ref={heading} tabIndex={-1} id="gift-dialog-title" className="text-xl font-semibold">{title}</h2>
      <button disabled={busy} onClick={requestClose} className="px-3">{onBack ? "Back to gift" : "Close"}</button>
    </header>
    {confirming ? <div className="p-4 space-y-3"><h3>Discard your changes?</h3><div className="flex gap-3"><button autoFocus className="px-3" disabled={busy} onClick={() => { setDiscarding(false); if (blocker.state === "blocked") blocker.reset() }}>Keep editing</button><button className="px-3 text-red-700" disabled={busy} onClick={() => { if (blocker.state === "blocked") blocker.proceed(); else onClose() }}>Discard changes</button></div></div> : null}
    <div hidden={confirming} className="min-h-0 overflow-y-auto">{children}</div>
  </dialog>
}
